import Field from "@/components/Field";
import Form from "@/components/Form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import DateTimePicker from "@/components/DateTimePicker";
import { useUpdateContractorVisitsMutation } from "@/modules/projects/components/contractors-visits/hooks/useUpdateContractorVisitsMutation";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback, useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { updateContractorVisit } from "@/schemas/contractorVisit";
import { UpdateContractorVisit } from "@/types/contractorVisit";
import TimeInput from "@/components/TimeInput";
import { ContractorVisitDate } from "./ContractorVisitDate";
import { useContractorVisitDateTimeDefaultValues } from "../hooks/useContractorVisitDateTimeDefaultValues";
import { useIsVisitEditableForRegionalRepresentative } from "../hooks/useIsVisitEditableForRegionalRepresentative";
import { NumberInput } from "@/components/ui/NumberInput";

interface ContractorVisitEditorDialogProps {
  visit?: ReadContractorVisit;
  isOpen: boolean;
  onClose: () => void;
}

export const ContractorVisitEditorDialog = memo(
  ({ visit, isOpen, onClose }: ContractorVisitEditorDialogProps) => {
    const { t } = useTranslation();
    const updateMutation = useUpdateContractorVisitsMutation();
    const isVisitEditableForRegionalRepresentative =
      useIsVisitEditableForRegionalRepresentative(visit?.editable_by_pr);

    const defaultValuesDateTimes =
      useContractorVisitDateTimeDefaultValues(visit);

    const defaultValues = useMemo(
      () => ({
        id: "",
        pr_planned_duration: "",
        kam_planned_duration: "",
        suggested_start_time: null,
        suggested_end_time: null,
        ...defaultValuesDateTimes,
      }),
      [defaultValuesDateTimes],
    );

    const form = useForm<UpdateContractorVisit>({
      resolver: zodResolver(updateContractorVisit),
      defaultValues,
    });

    useEffect(() => {
      if (visit) {
        const formData = {
          id: visit.id,
          pr_planned_duration: visit.pr_planned_duration,
          kam_planned_duration: visit.kam_planned_duration ?? "",
          suggested_start_time: visit.suggested_start_time,
          suggested_end_time: visit.suggested_end_time,
          start_datetime: defaultValues.start_datetime,
          end_datetime: defaultValues.end_datetime,
        };

        if (visit.start_datetime) {
          Object.assign(formData, { start_datetime: visit.start_datetime });
        }

        if (visit.end_datetime) {
          Object.assign(formData, { end_datetime: visit.end_datetime });
        }

        form.reset(formData);
      }
    }, [visit, form, defaultValues]);

    const handleSubmit: SubmitHandler<UpdateContractorVisit> = useCallback(
      async (data) => {
        // Schema handles empty string to null conversion for all fields
        const validatedData = updateContractorVisit.parse(data);
        await updateMutation.mutateAsync([validatedData]);
        onClose();
      },
      [updateMutation, onClose],
    );

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t("projectContractorVisits.editDialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t("projectContractorVisits.editDialog.description")}
              <ContractorVisitDate date={visit?.date} />
            </DialogDescription>
          </DialogHeader>
          <Form form={form} handleSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {isVisitEditableForRegionalRepresentative && (
                <Field
                  name="pr_planned_duration"
                  label={t(
                    "projectContractorVisits.fields.pr_planned_duration",
                  )}
                  render={({ field }) => (
                    <NumberInput
                      {...field}
                      disabled={updateMutation.isPending}
                    />
                  )}
                />
              )}
              <Field
                name="kam_planned_duration"
                label={t("projectContractorVisits.fields.kam_planned_duration")}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    step="0.1"
                    disabled={updateMutation.isPending}
                  />
                )}
              />
              <Field
                name="suggested_start_time"
                label={t("projectContractorVisits.fields.suggested_start_time")}
                render={({ field }) => (
                  <TimeInput {...field} disabled={updateMutation.isPending} />
                )}
              />
              <Field
                name="suggested_end_time"
                label={t("projectContractorVisits.fields.suggested_end_time")}
                render={({ field }) => (
                  <TimeInput {...field} disabled={updateMutation.isPending} />
                )}
              />
              <Field
                name="start_datetime"
                label={t("projectContractorVisits.fields.start_datetime")}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    className="w-full"
                    disabled={updateMutation.isPending}
                  />
                )}
              />
              <Field
                name="end_datetime"
                label={t("projectContractorVisits.fields.end_datetime")}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    className="w-full"
                    disabled={updateMutation.isPending}
                  />
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={updateMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending
                  ? t("common.saving")
                  : t("common.save")}
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    );
  },
);
